import { config } from '@shopee-user/platform';
import {
  isFeatureEnabled as isStaticFeatureEnabled,
  Feature as StaticFeature,
} from '@shopee-user/static-feature-toggle';

import { ClientID } from './constants';
import { URLMatchType } from './constants';
import migrationTracker from '../scripts/migration-tracker.json';

const isMigrateRedirectUrlConfigurationToStaticAsset =
  isStaticFeatureEnabled(
    StaticFeature.MigrateRedirectUrlConfigurationToStaticAsset,
    false
  ) && migrationTracker[config.BUSINESS_TARGET];

type RedirectionConfig =
  | {
      matchType: URLMatchType.ExactMatch;
      urlList: string[];
    }
  | {
      matchType: URLMatchType.HostnameRegex;
      regexList: Array<RegExp>;
    }
  | {
      matchType: URLMatchType.HostnameAndPathnameRegex;
      regexList: Array<{ hostname: RegExp; pathname: RegExp }>;
    };

type RedirectionConfigByClientId = Record<string, RedirectionConfig>;

type RegExpObject = { source: string; flags: string } | string;

function buildRegExp(item: RegExpObject): RegExp {
  if (typeof item === 'string') {
    return new RegExp(item);
  }
  return new RegExp(item.source, item.flags);
}
function parseConfig(config: any): RedirectionConfig | undefined {
  switch (config.matchType) {
    case URLMatchType.ExactMatch:
      return config;
    case URLMatchType.HostnameRegex:
      return {
        matchType: config.matchType,
        regexList: (config.regexList || []).map((item: RegExpObject) =>
          buildRegExp(item)
        ),
      };
    case URLMatchType.HostnameAndPathnameRegex:
      return {
        matchType: config.matchType,
        regexList: (config.regexList || []).map(
          (item: { hostname: RegExpObject; pathname: RegExpObject }) => ({
            hostname: buildRegExp(item.hostname),
            pathname: buildRegExp(item.pathname),
          })
        ),
      };
    default:
      return undefined;
  }
}
export function parseAssetValue(
  asset: any
): RedirectionConfigByClientId | undefined {
  try {
    if (asset && typeof asset === 'object') {
      const ret: RedirectionConfigByClientId = {};

      Object.keys(asset).forEach(clientId => {
        const config = parseConfig(asset[clientId]);
        if (config) {
          ret[clientId] = config;
        }
      });

      return ret;
    }
  } catch (e) {
    return undefined;
  }
  return undefined;
}

const HARDCODED_CLIENT_ID_TO_REDIRECTION_CONFIG: Record<
  string,
  | {
      matchType: URLMatchType.ExactMatch;
      urlList: string[];
    }
  | {
      matchType: URLMatchType.HostnameRegex;
      regexList: Array<RegExp>;
    }
  | {
      matchType: URLMatchType.HostnameAndPathnameRegex;
      regexList: Array<{ hostname: RegExp; pathname: RegExp }>;
    }
> = {
  [ClientID.SPXInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/, // for testing
      /^spx\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(vn|co\.th|sg|co\.id|com\.br|ph|com\.my)$/,
      /^onlinestore\.(test\.|uat\.|staging\.|stable\.)?spx\.sg$/,
    ],
  },
  [ClientID.SPXSeller]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    regexList: [
      {
        hostname: /^(test\.|uat\.|staging\.)?spx\.(vn|co\.th|sg|co\.id|ph|com\.my|com\.br)$/,
        pathname: /^\/shipment\/account\//,
      },
      {
        hostname: /^(test\.|uat\.|staging\.)?shopeexpress\.(com\.br)$/,
        pathname: /.?/,
      },
      {
        hostname: /^account\.(test\.|uat\.|staging\.)?spx\.(vn|tw|ph|sg|co\.th|com\.my|co\.id|com\.br)$/,
        pathname: /.?/,
      },
      {
        hostname: /^onlinestore\.(test\.|uat\.|staging\.|stable\.)?spx\.(sg|co\.id|com\.my|ph)$/,
        pathname: /^\/shopify\/account\//,
      },
      {
        hostname: /^(test\.|uat\.|staging\.)?spx\.(vn|co\.th|sg|co\.id|ph|com\.my|com\.br)$/,
        pathname: /^\/m\/reward-shop\/detail/,
      },
      {
        hostname: /^(test\.|uat\.|staging\.)?spx\.(vn|co\.th|sg|co\.id|ph|com\.my|com\.br)$/,
        pathname: /^\/spx-admin\/promotion\/my-membership/,
      },
    ],
  },
  [ClientID.SLSInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^sls\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.SLS]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [/^logistics\.(test\.|uat\.|staging\.)?shopee\.com$/],
  },
  // NOTE: Both Mystock and Shopee Partner uses this, since they use the same partner site
  [ClientID.Partner]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^partner\.(dev\.|test\.|uat\.|staging\.|test-stable\.)?shopee\.(co\.id|sg|com\.my|ph|vn|co\.th)$/,
    ],
  },
  [ClientID.ShopeePartnerInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^shopee-partner\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(co\.id|sg|com\.my|ph|vn|co\.th)$/,
      /^partner\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(co\.id|sg|com\.my|ph|vn|co\.th)$/,
    ],
  },
  [ClientID.MyStockInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^mystock\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(co\.id|vn)$/,
    ],
  },
  [ClientID.BrandIp]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [/^brandipp\.(test\.|uat\.|staging\.)?shopee\.com$/],
  },
  [ClientID.BrandIpInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^brandipp\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.OpenPlatform]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    regexList: [
      {
        hostname: /^open\.(test\.|uat\.|staging\.)?shopee\.(com|com\.br|cn)$/,
        pathname: /^\/opservice\/api\/v1\/account\/init_session/,
      },
    ],
  },
  [ClientID.OpenPlatformInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^open\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.PartnerVoucherInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^partnervoucher\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(co\.id|sg|ph|com\.my|co\.th|vn|tw|com\.br|com\.mx|com\.co|cl)$/,
    ],
  },
  [ClientID.PartnerVoucher]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^partnervoucher\.(test\.|uat\.|staging\.)?shopee\.(co\.id|sg|ph|com\.my|co\.th|vn|tw|com\.br|com\.mx|com\.co|cl)$/,
    ],
  },
  [ClientID.SpxAgencyInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^spxagency\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(ph|com\.br|sg|vn|co\.th|com\.my|tw|co\.id)$/,
    ],
  },
  [ClientID.SpxAgency]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^logistics\.(test\.|uat\.|staging\.)?myagencyservice\.(com\.br|ph|sg|vn|in\.th|my|tw|id)$/,
      /^accounts\.(test\.|uat\.|staging\.)?myagencyservice\.(com\.br|ph|sg|vn|in\.th|my|tw|id)$/,
      /^(test\.|uat\.|staging\.)?myagencyservice\.(ph|com\.br|sg|vn|in\.th|my|tw|id)$/,
    ],
  },
  [ClientID.TwsInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^tws\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.Tws]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^tws(-uat)?\.shopeesz\.com$/,
      /^tws\.(test\.|uat\.|staging\.)?shopeemobile\.com$/,
      /^tws\.dev\.shopeemobile\.com$/,
    ],
  },
  [ClientID.WarehouseManagementSystemInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^wms\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.WarehouseManagementSystem]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    regexList: [
      {
        hostname: /^wms\.ssc\.(test\.|uat\.|staging\.)?shopee\.(sg|co\.id|com\.my|ph|co\.th|vn|tw|cn|com\.mx|com\.br|kr)$/,
        pathname: /^\/v2\/tob\/callback$/,
      },
      {
        hostname: /^wms-ds\.ssc\.(test\.|uat\.|staging\.)?shopee\.(sg|co\.id|com\.my|ph|co\.th|vn|tw|cn|com\.mx|com\.br|kr)$/,
        pathname: /^\/v2\/tob\/callback$/,
      },
    ],
  },
  [ClientID.WorkforceManagementInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^wfm\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.WorkforceManagement]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [/^wfm\.ssc\.(test\.|uat\.|staging\.)?shopee\.com(\.mx|\.br)?$/],
  },
  [ClientID.WorkforceAgencyInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^wfm-agency\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.com$/,
    ],
  },
  [ClientID.WorkforceAgency]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [/^wfm\.ssc\.(test\.|uat\.|staging\.)?shopee\.com(\.mx|\.br)?$/],
  },
  [ClientID.FmsInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^fms\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(com\.br|com\.my|co\.id|co\.th|vn|ph|sg|tw)$/,
    ],
  },
  [ClientID.Fms]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    regexList: [
      {
        hostname: /^spx\.(test\.|uat\.|staging\.|dev\.)?shopee\.(com\.br|com\.my|co\.id|co\.th|vn|ph|sg|tw)$/,
        pathname: /^\/(api\/admin\/basicserver\/ops_tob_login|common-basic\/basic-mobile\/forget-password-success)$/,
      },
      {
        hostname: /^spx\.shopee\.systems$/,
        pathname: /.?/,
      },
      {
        hostname: /^(test\.)?spx-fms\.co\.id$/,
        pathname: /^\/api\/admin\/basicserver\/ops_tob_login$/,
      },
      {
        hostname: /^temp\.(test\.|uat\.|staging\.)?myagencyservice\.(com\.br|sg|id|vn|ph|my|in\.th)$/,
        pathname: /.?/,
      },
    ],
  },
  [ClientID.Sandbox]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^sandbox\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(co\.id|co\.th|sg)$/,
    ],
  },
  [ClientID.CityLinkExpressInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^citylinkexpress\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(com\.my)$/,
    ],
  },
  [ClientID.CityLinkExpress]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    regexList: [
      {
        hostname: /^account\.(test\.|uat\.|staging\.)?citylinkexpress-ecomm\.com$/,
        pathname: /.?/,
      },
      {
        hostname: /^verify\.(test\.|uat\.|staging\.)?citylinkexpress-ecomm\.com$/,
        pathname: /^\/verify\/ivs$/,
      },
      {
        hostname: /^(www\.)?(test\.|uat\.|staging\.)?citylinkexpress-ecomm\.com$/,
        pathname: /^\/shipment\/account\/api\/login$/,
      },
    ],
  },
  [ClientID.FoodDriver]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    // Currently Food Driver only supports MY and ID
    regexList: [
      {
        hostname: /^(www\.)?(test\.|uat\.)?shopeefood\.co\.id$/,
        pathname: /^(\/en)?\/drivers\/registration-form$/,
      },
      {
        hostname: /^(www\.)?(test\.|uat\.)?shopeefood\.com\.my$/,
        pathname: /^(\/bm)?\/drivers\/registration-form$/,
      },
    ],
  },
  [ClientID.ServicePointInternal]: {
    matchType: URLMatchType.HostnameRegex,
    regexList: [
      /^localhost$/,
      /^service-point\.business\.accounts\.(test\.|uat\.|staging\.)?shopee\.(com\.br|co\.id|com\.my|ph|sg|co\.th|vn)$/,
    ],
  },
  [ClientID.ServicePoint]: {
    matchType: URLMatchType.HostnameAndPathnameRegex,
    regexList: [
      {
        hostname: /^sp\.spx\.(test\.|uat\.|staging\.)?shopee\.(com\.br|co\.id|com\.my|ph|sg|co\.th|vn)$/,
        pathname: /^\/sp-api\/tob\/auth\/callback$/,
      },
    ],
  },
};

// this is a mapping between ClientID and Redirection config
export const CLIENT_ID_TO_REDIRECTION_CONFIG: RedirectionConfigByClientId = isMigrateRedirectUrlConfigurationToStaticAsset
  ? parseAssetValue(window.__ASSETS__?.TOB_REDIRECTION_ALLOWLIST) || {}
  : HARDCODED_CLIENT_ID_TO_REDIRECTION_CONFIG;
